/**
 * Field Label Registry
 *
 * Maintains a mapping between field names (keys) and their human-readable labels.
 * Used to display clean, readable error messages instead of raw field names.
 *
 * Usage:
 * 1. Register fields when form modules load: registerFieldLabels(fields)
 * 2. Get readable label: getFieldLabel('howlongcanyoustand') => "How long can you stand?"
 */

// In-memory registry mapping field names to display labels
const fieldLabelMap = new Map();

// Common label overrides for fields that may not have proper labels
const COMMON_LABEL_OVERRIDES = {
  firstname: 'First Name',
  lastname: 'Last Name',
  email: 'Email Address',
  phone: 'Phone Number',
  postalcode: 'Zip Code',
  dob: 'Date of Birth',
  dateofbirth: 'Date of Birth',
  gender: 'Gender',
  tc: 'Terms & Conditions',
  address: 'Address',
  address_street: 'Street Address',
  address_city: 'City',
  address_state: 'State',
  address_suite: 'Suite/Apt Number',
  currentpainlevel: 'Current Pain Level',
  bestpainlevel: 'Best Pain Level',
  worstpainlevel: 'Worst Pain Level',
  dl_front: "Driver's License (Front)",
  dl_back: "Driver's License (Back)",
  primary_front: 'Primary Insurance (Front)',
  primary_back: 'Primary Insurance (Back)',
  secondary_front: 'Secondary Insurance (Front)',
  secondary_back: 'Secondary Insurance (Back)',
  tertiary_front: 'Tertiary Insurance (Front)',
  tertiary_back: 'Tertiary Insurance (Back)',
  medicationdata: 'Medications',
  pharmacydata: 'Pharmacy',
};

/**
 * Normalize field name for consistent lookup
 * Converts to lowercase, removes underscores/spaces
 */
const normalizeKey = (key) => {
  if (!key) return '';
  return key.toLowerCase().replace(/[_\s-]/g, '');
};

/**
 * Register a single field with its label
 * @param {string} fieldName - The field name/key
 * @param {string} label - Human-readable label
 */
export const registerField = (fieldName, label) => {
  if (!fieldName || !label) return;
  const normalizedKey = normalizeKey(fieldName);
  fieldLabelMap.set(normalizedKey, label);
};

/**
 * Register all fields from a module's field array
 * @param {Array} fields - Array of field definitions from API
 */
export const registerFieldLabels = (fields) => {
  if (!Array.isArray(fields)) return;

  fields.forEach((field, index) => {
    const fieldName = field.name || field.labelName || field.label || `field_${index}`;
    const label = field.labelName || field.label || field.name;

    if (fieldName && label) {
      registerField(fieldName, label);

      // Also register normalized version
      const normalizedName = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (normalizedName !== normalizeKey(fieldName)) {
        registerField(normalizedName, label);
      }
    }

    // Handle address subfields
    if (field.type === 'address') {
      const addressConfig = field.addressConfig || {};
      const baseName = normalizeKey(fieldName);

      if (addressConfig.streetAddress?.label) {
        registerField(`${baseName}_street`, addressConfig.streetAddress.label);
      }
      if (addressConfig.suiteApt?.label) {
        registerField(`${baseName}_suite`, addressConfig.suiteApt.label);
      }
      if (addressConfig.city?.label) {
        registerField(`${baseName}_city`, addressConfig.city.label);
      }
      if (addressConfig.state?.label) {
        registerField(`${baseName}_state`, addressConfig.state.label);
      }
    }
  });
};

/**
 * Convert camelCase or concatenated text to readable format
 * @param {string} str - String to convert
 * @returns {string} - Human-readable string
 */
const smartCapitalize = (str) => {
  if (!str) return '';

  // First, try to split on common word boundaries
  let result = str
    // Insert space before capitals in camelCase
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Insert space before numbers
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    // Replace underscores and hyphens with spaces
    .replace(/[_-]/g, ' ')
    // Clean up multiple spaces
    .replace(/\s+/g, ' ')
    .trim();

  // Capitalize first letter of each word
  result = result
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  // Fix common abbreviations
  result = result
    .replace(/\bId\b/g, 'ID')
    .replace(/\bDob\b/g, 'DOB')
    .replace(/\bSsn\b/g, 'SSN')
    .replace(/\bTc\b/g, 'T&C')
    .replace(/\bDl\b/g, "Driver's License");

  return result;
};

/**
 * Attempt to intelligently split concatenated words
 * Uses dictionary matching for common medical/form terms
 */
const splitConcatenatedWords = (text) => {
  const commonWords = [
    'how', 'long', 'can', 'you', 'stand', 'without', 'the', 'need', 'to', 'move',
    'what', 'is', 'your', 'are', 'do', 'does', 'have', 'has', 'when', 'where',
    'why', 'which', 'who', 'please', 'describe', 'explain', 'list', 'select',
    'enter', 'provide', 'pain', 'level', 'current', 'best', 'worst', 'average',
    'date', 'of', 'birth', 'first', 'last', 'name', 'middle', 'email', 'phone',
    'address', 'street', 'city', 'state', 'country', 'postal', 'code', 'zip',
    'medical', 'history', 'family', 'surgical', 'medication', 'allergy', 'allergies',
    'insurance', 'primary', 'secondary', 'tertiary', 'driver', 'license',
    'emergency', 'contact', 'relationship', 'symptoms', 'diagnosis', 'treatment',
    'walking', 'sitting', 'standing', 'lying', 'down', 'sleep', 'work', 'exercise',
    'morning', 'afternoon', 'evening', 'night', 'daily', 'weekly', 'monthly',
    'never', 'rarely', 'sometimes', 'often', 'always', 'mild', 'moderate', 'severe',
    'front', 'back', 'side', 'left', 'right', 'both', 'upper', 'lower', 'middle',
    'head', 'neck', 'shoulder', 'arm', 'elbow', 'wrist', 'hand', 'finger',
    'chest', 'abdomen', 'hip', 'leg', 'knee', 'ankle', 'foot', 'toe', 'spine',
    'any', 'other', 'additional', 'comments', 'notes', 'information',
    'yes', 'no', 'not', 'applicable', 'unknown', 'male', 'female', 'other',
    'single', 'married', 'divorced', 'widowed', 'employed', 'unemployed', 'retired',
  ];

  const lowerText = text.toLowerCase();
  const words = [];
  let remaining = lowerText;

  // Greedy matching - try to match longest words first
  while (remaining.length > 0) {
    let matched = false;

    // Try matching from longest to shortest
    for (let len = Math.min(remaining.length, 12); len >= 2; len--) {
      const candidate = remaining.substring(0, len);
      if (commonWords.includes(candidate)) {
        words.push(candidate);
        remaining = remaining.substring(len);
        matched = true;
        break;
      }
    }

    // If no match found, take one character and continue
    if (!matched) {
      if (words.length > 0 && words[words.length - 1].length < 3) {
        // Append to previous word if it's short
        words[words.length - 1] += remaining[0];
      } else {
        words.push(remaining[0]);
      }
      remaining = remaining.substring(1);
    }
  }

  // Clean up single character artifacts
  const cleanedWords = [];
  for (let i = 0; i < words.length; i++) {
    if (words[i].length === 1 && i < words.length - 1) {
      // Merge single char with next word if it makes sense
      cleanedWords.push(words[i] + words[i + 1]);
      i++;
    } else if (words[i].length > 1) {
      cleanedWords.push(words[i]);
    }
  }

  return cleanedWords.join(' ');
};

/**
 * Get human-readable label for a field name
 * @param {string} fieldName - The field name/key
 * @returns {string} - Human-readable label
 */
const resolveFieldLabel = (fieldName) => {
  if (!fieldName) return 'Unknown Field';

  const normalizedKey = normalizeKey(fieldName);

  // 1. Check registry first (labels from API)
  if (fieldLabelMap.has(normalizedKey)) {
    return fieldLabelMap.get(normalizedKey);
  }

  // 2. Check common overrides
  if (COMMON_LABEL_OVERRIDES[normalizedKey]) {
    return COMMON_LABEL_OVERRIDES[normalizedKey];
  }

  // 3. Check if it's already a readable label (contains spaces)
  if (fieldName.includes(' ')) {
    return fieldName;
  }

  // 4. Try smart capitalization for camelCase
  const smartResult = smartCapitalize(fieldName);
  if (smartResult.includes(' ') || smartResult !== fieldName) {
    return smartResult;
  }

  // 5. Try splitting concatenated words (for all-lowercase fields)
  const splitResult = splitConcatenatedWords(fieldName);
  if (splitResult.includes(' ')) {
    return smartCapitalize(splitResult);
  }

  // 6. Fallback: just capitalize first letter
  return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
};

// Display as "Zip Code" even when the API label says "Postal Code"
const toDisplayLabel = (label) =>
  typeof label === 'string' ? label.replace(/postal\s*code/gi, (m) =>
    m === m.toLowerCase() ? 'zip code' : m === m.toUpperCase() ? 'ZIP CODE' : 'Zip Code') : label;

export const getFieldLabel = (fieldName) => toDisplayLabel(resolveFieldLabel(fieldName));

/**
 * Format validation errors for display
 * @param {Object} errors - Formik errors object { fieldName: errorMessage }
 * @returns {Array} - Array of { field, label, message } objects
 */
export const formatValidationErrors = (errors) => {
  if (!errors || typeof errors !== 'object') return [];

  return Object.entries(errors).map(([fieldName, errorMessage]) => {
    const label = getFieldLabel(fieldName);

    // Clean up the error message
    let message = errorMessage;
    if (typeof message === 'string') {
      // Remove redundant field name from message if present
      message = message.replace(new RegExp(`^${fieldName}\\s*`, 'i'), '');
      message = message.replace(/^is required$/i, 'is required');
    }

    return {
      field: fieldName,
      label,
      message: message || 'is required',
    };
  });
};

/**
 * Format errors as a readable string for display
 * @param {Object} errors - Formik errors object
 * @returns {string} - Formatted error string with bullets
 */
export const formatErrorsAsString = (errors) => {
  const formatted = formatValidationErrors(errors);

  return formatted.map(({ label, message }) => {
    // Check if message already contains a complete sentence
    if (message.toLowerCase().startsWith('please') ||
        message.toLowerCase().startsWith('you must') ||
        message.includes(label)) {
      return `• ${message}`;
    }
    // Format as "Label is required" or "Label: error message"
    if (message.toLowerCase() === 'is required') {
      return `• "${label}" is required`;
    }
    return `• ${label}: ${message}`;
  }).join('\n');
};

/**
 * Clear the registry (useful for testing or page navigation)
 */
export const clearFieldRegistry = () => {
  fieldLabelMap.clear();
};

/**
 * Get current registry size (for debugging)
 */
export const getRegistrySize = () => fieldLabelMap.size;

export default {
  registerField,
  registerFieldLabels,
  getFieldLabel,
  formatValidationErrors,
  formatErrorsAsString,
  clearFieldRegistry,
  getRegistrySize,
};
